// What the property page shows at a glance, derived from the owner's
// report-document payload. Pure functions; every field is optional and a
// missing one simply leaves its line out.
import { cleanPhotoName } from "../../utils/photos";
import { reasonLabel } from "../TouchupsPage/TouchupPicker";

/** @param {Record<string, any> | null | undefined} content @param {string} type */
const sectionsOf = (content, type) =>
  /** @type {Record<string, any>[]} */ (content?.sections || []).filter(
    (s) => s?.__type__ === type
  );
/** @param {Record<string, any> | null | undefined} content @param {string} type */
const sectionOf = (content, type) => sectionsOf(content, type)[0] || null;

/** Numbers arrive as numbers or numeric strings ("4.0"); anything else is null. */
export const asNumber = (/** @type {unknown} */ v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
};

/** "kitchen" / "living_room" -> "Kitchen" / "Living room". */
export const humanize = (/** @type {string | null | undefined} */ s) => {
  const words = (s || "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/**
 * The photos in the recommended order: hero first, then the sequence the
 * photo strategy proposes, then anything it left out in manifest order.
 * @param {Record<string, any> | null | undefined} content
 * @param {Record<string, string> | null | undefined} manifest
 */
export const recommendedOrder = (content, manifest) => {
  const names = Object.keys(manifest || {});
  const strategy = sectionOf(content, "PhotoSequenceSection")?.strategy || {};
  /** @type {string[]} */
  const ordered = [];
  const push = (/** @type {unknown} */ raw) => {
    const n = cleanPhotoName(/** @type {string} */ (raw));
    if (n && names.includes(n) && !ordered.includes(n)) ordered.push(n);
  };
  push(strategy.lead_image);
  (strategy.complete_sequence || []).forEach(push);
  (strategy.first_five_sequence || []).forEach(push);
  names.forEach(push);
  return ordered;
};

/** The hero pick, when the strategy named one that exists in the manifest. */
export const heroPhoto = (
  /** @type {Record<string, any> | null | undefined} */ content,
  /** @type {Record<string, string> | null | undefined} */ manifest
) => {
  const n = cleanPhotoName(sectionOf(content, "PhotoSequenceSection")?.strategy?.lead_image);
  return n && manifest?.[n] ? n : null;
};

/**
 * Per-photo notes from the photo review: a short label (the room) and the
 * findings that a touch-up could address, as words.
 * @param {Record<string, any> | null | undefined} content
 * @returns {Record<string, { label: string, findings: string[] }>}
 */
export const photoNotes = (content) => {
  /** @type {Record<string, { label: string, findings: string[] }>} */
  const notes = {};
  const note = (/** @type {string} */ n) => {
    if (!notes[n]) notes[n] = { label: "", findings: [] };
    return notes[n];
  };
  for (const section of sectionsOf(content, "PhotoAnalysisSection")) {
    const a = section.assessment || {};
    const room = humanize(section.room_type);
    for (const raw of a.photos || []) {
      const n = cleanPhotoName(raw);
      if (n && !note(n).label) note(n).label = room;
    }
    for (const edit of a.machine_edits || []) {
      const n = cleanPhotoName(edit?.photo_filename);
      if (!n) continue;
      for (const item of edit.edits || []) {
        const label = reasonLabel(item?.edit_type);
        if (label && !note(n).findings.includes(label)) note(n).findings.push(label);
      }
    }
  }
  return notes;
};

/**
 * The Quick CMA in one line: the suggested range, the anchor, the list
 * price, the verdict, and the span the scale needs to place all of them.
 * @param {Record<string, any> | null | undefined} content
 */
export const priceGlance = (content) => {
  const cma = sectionOf(content, "ComparableAnalysisSection");
  if (!cma) return null;
  const low = asNumber(cma.suggested_range_low);
  const high = asNumber(cma.suggested_range_high);
  const anchor = asNumber(cma.anchor_price);
  const list = asNumber(content?.property_details?.listing_price);
  const verdict = String(cma.pricing_verdict || "").trim();
  const why = String((cma.pricing_justification || [])[0] || "").trim();
  if (low == null && high == null && !verdict) return null;
  const comps = [...(cma.comparable_properties || []), ...(cma.supporting_comparables || [])]
    .map((p) => asNumber(p?.price))
    .filter((v) => v != null);
  const marks = /** @type {number[]} */ (
    [...comps, low, high, anchor, list].filter((v) => v != null)
  );
  const spanLow = marks.length ? Math.min(...marks) : null;
  const spanHigh = marks.length ? Math.max(...marks) : null;
  const tone = /above|over|high|aggressive/i.test(verdict)
    ? "high"
    : /below|under|low|conservative/i.test(verdict)
      ? "low"
      : "ok";
  return {
    low,
    high,
    anchor,
    list,
    verdict,
    why,
    tone,
    spanLow,
    spanHigh,
    hasComps: comps.length > 0,
  };
};

/** Target buyer and the one thing to lead with. */
export const strategyGlance = (/** @type {Record<string, any> | null | undefined} */ content) => {
  const summary = sectionOf(content, "ExecutiveSummarySection") || {};
  const buyer = String(summary.target_buyer || "").trim();
  const opportunity = String(
    summary.primary_opportunity || (content?.cover_page_data?.top_opportunities || [])[0] || ""
  ).trim();
  return buyer || opportunity ? { buyer, opportunity } : null;
};

/** Beds, baths, square feet, year built, lot — whichever are known. */
export const factsGlance = (/** @type {Record<string, any> | null | undefined} */ content) => {
  const d = content?.property_details || {};
  const trim = (/** @type {number | null} */ n) =>
    n == null ? null : String(n).replace(/\.0$/, "");
  const lot = asNumber(d.lot_size);
  const facts = [
    { label: "Beds", value: trim(asNumber(d.bedrooms)) },
    { label: "Baths", value: trim(asNumber(d.bathrooms)) },
    { label: "Sq ft", value: asNumber(d.square_feet)?.toLocaleString("en-US") ?? null },
    { label: "Built", value: trim(asNumber(d.year_built)) },
    {
      label: "Lot",
      value: lot == null ? null : lot <= 50 ? `${lot} ac` : `${lot.toLocaleString("en-US")} sq ft`,
    },
  ];
  return facts.filter((f) => f.value);
};

/** The files that exist for this listing, by their product names. */
export const packageGlance = (/** @type {Record<string, any> | null | undefined} */ doc) => {
  const a = doc?.artifacts || {};
  const ready = [
    a.report_pdf?.available && "Branded PDF",
    a.deck?.available && "Seller Presentation",
    a.one_pager?.available && "One-pager",
    (doc?.social_posts || []).length > 0 && "Social set",
  ].filter(Boolean);
  return /** @type {string[]} */ (ready);
};

export const money = (/** @type {number} */ n) => `$${Math.round(n).toLocaleString("en-US")}`;
export const moneyShort = (/** @type {number} */ n) =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, "")}M` : `$${Math.round(n / 1000)}k`;
